import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiUpload, mdiAlertCircleOutline, mdiInformationOutline, mdiFileDelimitedOutline } from '@mdi/js'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { apiClient } from '../../api/client'
import type { components } from '../../api/schema'

type FolderLayoutImportResponse = components['schemas']['FolderLayoutImportResponse']
type FolderChange = components['schemas']['FolderLayoutImportFolderChange']

/** A layout already present in the organization, offered as a CSV target. */
export interface ImportTargetLayout {
    code: string
    label: string
}

function errorMessage(error: unknown): string {
    if (!error) return ''
    if (typeof error === 'string') return error
    if (typeof error === 'object') {
        const candidate = error as { message?: string; error?: string; code?: string }
        return candidate.message || candidate.error || candidate.code || JSON.stringify(error)
    }
    return String(error)
}

/**
 * A file is treated as CSV when its name says so or when its content does not
 * start like JSON — the same rule the backend applies, so the dialog's target
 * layout field appears exactly when the server will require it.
 */
function looksLikeCSV(fileName: string, text: string): boolean {
    if (fileName.toLowerCase().endsWith('.csv')) return true
    const trimmed = text.replace(/^\uFEFF/, '').trimStart()
    return trimmed.length > 0 && trimmed[0] !== '{' && trimmed[0] !== '['
}

/** One preview row: `path` (or code) plus, for updates, what differs. */
function ChangeRow({ change, fieldLabel }: { change: FolderChange; fieldLabel: (f: string) => string }) {
    const { t } = useTranslation('folders')
    const fields = change.changed_fields || []
    return (
        <li
            className="flex flex-wrap items-baseline gap-2 text-sm"
            data-testid={`import-change-row-${change.code}`}
        >
            <span className="font-mono text-xs">{change.code}</span>
            {/* A root folder's path IS its code — showing both just reads as a
                stutter, so the path only appears when it adds something. */}
            {change.path && change.path !== change.code && (
                <span className="truncate text-muted-foreground">{change.path}</span>
            )}
            {fields.length > 0 && (
                <span className="text-xs text-muted-foreground">
                    {t('import.changed_fields', { fields: fields.map(fieldLabel).join(', ') })}
                </span>
            )}
        </li>
    )
}

function ChangeGroup({
    title,
    changes,
    testId,
    fieldLabel,
}: {
    title: string
    changes: FolderChange[]
    testId: string
    fieldLabel: (f: string) => string
}) {
    if (changes.length === 0) return null
    return (
        <div data-testid={testId}>
            <p className="text-sm font-medium">{title}</p>
            <ul className="mt-1 space-y-1">
                {changes.map(change => (
                    <ChangeRow key={change.code} change={change} fieldLabel={fieldLabel} />
                ))}
            </ul>
        </div>
    )
}

interface FolderLayoutImportDialogProps {
    org: string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Existing layouts, offered as CSV import targets. */
    layouts?: ImportTargetLayout[]
    /**
     * Import into ONE known layout (the layout detail page's entry point). The
     * target is then pre-locked: no free-text code field, and the code is sent
     * for JSON files too, which retargets a single-layout export at this layout.
     */
    lockedLayoutCode?: string
    /** Called once the real (non dry-run) import succeeded. */
    onImported: (result: FolderLayoutImportResponse) => void
}

/**
 * Two-step import of a folder-layout file: pick a file, see exactly what the
 * import would do (dry run), then confirm.
 *
 * Two formats are accepted. A JSON export can carry several layouts, so the
 * dry-run summary is per layout. A customer CSV (one row per folder, hierarchy
 * in the `path` column) carries folders only and therefore needs an explicit
 * target layout, which is created when its code is new. In both cases folders
 * that exist here but are absent from the file are reported as "kept" and are
 * never deleted.
 */
export function FolderLayoutImportDialog({ org, open, onOpenChange, layouts = [], lockedLayoutCode, onImported }: FolderLayoutImportDialogProps) {
    const { t } = useTranslation('folders')
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [fileName, setFileName] = useState<string | null>(null)
    const [payload, setPayload] = useState<unknown>(null)
    const [csvText, setCsvText] = useState<string | null>(null)
    const [layoutCode, setLayoutCode] = useState('')
    const [preview, setPreview] = useState<FolderLayoutImportResponse | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isBusy, setIsBusy] = useState(false)

    const isCSV = csvText !== null
    // In locked mode the target never comes from the user.
    const effectiveLayoutCode = lockedLayoutCode ?? layoutCode

    const reset = useCallback(() => {
        setFileName(null)
        setPayload(null)
        setCsvText(null)
        setLayoutCode('')
        setPreview(null)
        setError(null)
        setIsBusy(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }, [])

    const handleOpenChange = useCallback((next: boolean) => {
        if (!next) reset()
        onOpenChange(next)
    }, [onOpenChange, reset])

    const runJSONImport = useCallback(async (body: unknown, dryRun: boolean) => {
        const response = await apiClient.POST(
            '/api/v1/organizations/{orgCode}/document-folders-layouts/import',
            {
                params: {
                    path: { orgCode: org },
                    // Locked mode retargets a single-layout file at THIS layout,
                    // so an export taken from anywhere lands where the user is.
                    query: { dry_run: dryRun, ...(lockedLayoutCode ? { layout_code: lockedLayoutCode } : {}) },
                },
                body: body as never,
            }
        )
        if (response.error) {
            // A 422 carries the same summary shape, with `errors` filled in.
            const failed = response.error as unknown as FolderLayoutImportResponse
            if (failed && Array.isArray(failed.errors) && failed.errors.length > 0) {
                throw new Error(failed.errors.join('; '))
            }
            throw new Error(errorMessage(response.error) || t('import.error_generic'))
        }
        return response.data as FolderLayoutImportResponse
    }, [org, lockedLayoutCode, t])

    const runCSVImport = useCallback(async (text: string, targetCode: string, dryRun: boolean) => {
        const response = await apiClient.POST(
            '/api/v1/organizations/{orgCode}/document-folders-layouts/import',
            {
                params: {
                    path: { orgCode: org },
                    query: { dry_run: dryRun, layout_code: targetCode },
                },
                body: text as never,
                bodySerializer: (b: unknown) => b as string,
                headers: { 'Content-Type': 'text/csv' },
            }
        )
        if (response.error) {
            const failed = response.error as unknown as FolderLayoutImportResponse
            if (failed && Array.isArray(failed.errors) && failed.errors.length > 0) {
                throw new Error(failed.errors.join('; '))
            }
            throw new Error(errorMessage(response.error) || t('import.error_generic'))
        }
        return response.data as FolderLayoutImportResponse
    }, [org, t])

    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        setError(null)
        setPreview(null)
        setPayload(null)
        setCsvText(null)
        setFileName(file.name)
        setIsBusy(true)
        try {
            const text = await file.text()
            if (looksLikeCSV(file.name, text)) {
                // The dry run waits for a target layout — a CSV does not name one.
                setCsvText(text)
            } else {
                const parsed = JSON.parse(text)
                setPayload(parsed)
                setPreview(await runJSONImport(parsed, true))
            }
        } catch (err) {
            setError(errorMessage(err))
        } finally {
            setIsBusy(false)
        }
    }, [runJSONImport])

    // A CSV needs its target layout before anything can be planned, so the dry
    // run re-runs whenever the code changes.
    useEffect(() => {
        if (csvText === null) return
        const code = effectiveLayoutCode.trim()
        if (code === '') {
            setPreview(null)
            return
        }

        let cancelled = false
        const timer = setTimeout(() => {
            setIsBusy(true)
            setError(null)
            runCSVImport(csvText, code, true)
                .then(result => { if (!cancelled) setPreview(result) })
                .catch(err => {
                    if (cancelled) return
                    setPreview(null)
                    setError(errorMessage(err))
                })
                .finally(() => { if (!cancelled) setIsBusy(false) })
        }, 300)

        return () => { cancelled = true; clearTimeout(timer) }
    }, [csvText, effectiveLayoutCode, runCSVImport])

    const handleConfirm = useCallback(async () => {
        setIsBusy(true)
        setError(null)
        try {
            const result = csvText !== null
                ? await runCSVImport(csvText, effectiveLayoutCode.trim(), false)
                : await runJSONImport(payload, false)
            onImported(result)
            handleOpenChange(false)
        } catch (err) {
            setError(errorMessage(err))
        } finally {
            setIsBusy(false)
        }
    }, [csvText, effectiveLayoutCode, payload, runCSVImport, runJSONImport, onImported, handleOpenChange])

    const downloadTemplate = useCallback(async () => {
        setError(null)
        try {
            const response = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/import/template.csv',
                // A blob, not text: `Response.text()` strips the BOM that makes
                // Excel read the file as UTF-8.
                { params: { path: { orgCode: org } }, parseAs: 'blob' }
            )
            if (response.error) throw new Error(errorMessage(response.error) || t('import.error_generic'))

            const url = window.URL.createObjectURL(response.data as unknown as Blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'folder-layout-template.csv'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (err) {
            setError(errorMessage(err))
        }
    }, [org, t])

    // Per-folder change list, flattened across layouts (locked mode always has
    // exactly one) and grouped by action so the preview reads as "what will
    // happen", not as a list to scan.
    const folderChanges: FolderChange[] = preview?.layouts?.flatMap(l => l.folder_changes || []) || []
    const createdChanges = folderChanges.filter(c => c.action === 'create')
    const updatedChanges = folderChanges.filter(c => c.action === 'update')
    const unchangedChanges = folderChanges.filter(c => c.action === 'unchanged')

    const fieldLabel = useCallback(
        (field: string) => {
            const key = `import.field_${field}`
            const label = t(key)
            // A field the backend grew but the UI does not know yet stays
            // readable instead of showing the raw i18n key.
            return label === key ? field : label
        },
        [t]
    )

    const unmatchedCount = preview?.layouts?.reduce((sum, l) => sum + (l.folders_unmatched?.length || 0), 0) || 0
    const unknownTypes = Array.from(new Set(
        preview?.layouts?.flatMap(l => l.unknown_document_type_codes || []) || []
    ))
    // A folder carries one document type, so a row listing several keeps only
    // the first — the rest come back here and must be shown, not swallowed.
    const droppedTypes = Array.from(new Set(
        preview?.layouts?.flatMap(l => l.dropped_document_type_codes || []) || []
    ))
    const canConfirm = !isBusy && preview !== null && (!isCSV || effectiveLayoutCode.trim() !== '')

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {/* A rich preview easily grows past the viewport; the dialog is
                capped and only its body scrolls, so Confirm stays reachable. */}
            <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
                <DialogHeader>
                    <DialogTitle>{t('import.dialog_title')}</DialogTitle>
                </DialogHeader>
                <div className="flex min-h-0 flex-1 flex-col gap-4" data-testid="import-layout-dialog">
                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
                        <p className="text-sm text-muted-foreground">{t('import.dialog_description')}</p>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/json,.json,text/csv,.csv"
                            onChange={(e) => { void handleFileChange(e) }}
                            className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm"
                            data-testid="import-layout-file"
                        />
                        {fileName && (
                            <p className="text-xs text-muted-foreground" data-testid="import-layout-filename">{fileName}</p>
                        )}

                        <button
                            type="button"
                            onClick={() => { void downloadTemplate() }}
                            className="flex items-center gap-1.5 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                            data-testid="import-layout-template-link"
                        >
                            <AppIcon path={mdiFileDelimitedOutline} size="xs" />
                            {t('import.template_link')}
                        </button>

                        {lockedLayoutCode && (
                            <p className="text-xs text-muted-foreground" data-testid="import-layout-locked-target">
                                {t('import.target_layout_locked', { code: lockedLayoutCode })}
                            </p>
                        )}

                        {isCSV && !lockedLayoutCode && (
                            <div className="space-y-1.5" data-testid="import-layout-target">
                                <Label htmlFor="import-layout-code">{t('import.target_layout_label')}</Label>
                                <Input
                                    id="import-layout-code"
                                    list="import-layout-code-options"
                                    value={layoutCode}
                                    onChange={(e) => setLayoutCode(e.target.value)}
                                    placeholder={t('import.target_layout_placeholder')}
                                    data-testid="import-layout-code"
                                />
                                <datalist id="import-layout-code-options">
                                    {layouts.map(l => (
                                        <option key={l.code} value={l.code}>{l.label}</option>
                                    ))}
                                </datalist>
                                <p className="text-xs text-muted-foreground">{t('import.target_layout_help')}</p>
                            </div>
                        )}

                        {error && (
                            <div
                                className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive"
                                role="status"
                                data-testid="import-layout-error"
                            >
                                <AppIcon path={mdiAlertCircleOutline} size="md" className="mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {preview && (
                            <div className="space-y-3" data-testid="import-layout-preview">
                                <div className="rounded-lg border p-3 text-sm">
                                    <p className="font-medium" data-testid="import-layout-summary">
                                        {t('import.summary', {
                                            layoutsCreated: preview.layouts_created,
                                            layoutsUpdated: preview.layouts_updated,
                                        })}
                                    </p>
                                    <p className="mt-1 text-muted-foreground" data-testid="import-layout-folder-summary">
                                        {t('import.folder_summary', {
                                            created: preview.folders_created,
                                            updated: preview.folders_updated,
                                            unchanged: preview.folders_unchanged,
                                        })}
                                    </p>
                                </div>

                                <ul className="space-y-1" data-testid="import-layout-list">
                                    {preview.layouts?.map(layout => (
                                        <li
                                            key={layout.code}
                                            className="flex flex-wrap items-baseline gap-2 rounded-md border px-3 py-2 text-sm"
                                            data-testid={`import-layout-row-${layout.code}`}
                                        >
                                            <span className="font-mono text-xs">{layout.code}</span>
                                            <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                                                {layout.action === 'create' ? t('import.action_create') : t('import.action_update')}
                                            </span>
                                            <span className="text-muted-foreground">
                                                {t('import.folder_summary', {
                                                    created: layout.folders_created,
                                                    updated: layout.folders_updated,
                                                    unchanged: layout.folders_unchanged,
                                                })}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                {folderChanges.length > 0 && (
                                    <div className="rounded-lg border" data-testid="import-layout-changes">
                                        <p className="border-b px-3 py-2 text-sm font-medium">
                                            {t('import.changes_title')}
                                        </p>
                                        <div className="max-h-64 space-y-3 overflow-y-auto p-3">
                                            <ChangeGroup
                                                title={t('import.changes_created', { count: createdChanges.length })}
                                                changes={createdChanges}
                                                testId="import-layout-created"
                                                fieldLabel={fieldLabel}
                                            />
                                            <ChangeGroup
                                                title={t('import.changes_updated', { count: updatedChanges.length })}
                                                changes={updatedChanges}
                                                testId="import-layout-updated"
                                                fieldLabel={fieldLabel}
                                            />
                                            {unchangedChanges.length > 0 && (
                                                <details data-testid="import-layout-unchanged">
                                                    <summary className="cursor-pointer text-sm text-muted-foreground">
                                                        {t('import.changes_unchanged', { count: unchangedChanges.length })}
                                                    </summary>
                                                    <ul className="mt-1 space-y-1">
                                                        {unchangedChanges.map(change => (
                                                            <ChangeRow
                                                                key={change.code}
                                                                change={change}
                                                                fieldLabel={fieldLabel}
                                                            />
                                                        ))}
                                                    </ul>
                                                </details>
                                            )}
                                            {createdChanges.length === 0 && updatedChanges.length === 0 && (
                                                <p className="text-sm text-muted-foreground" data-testid="import-layout-no-changes">
                                                    {t('import.changes_none')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {(preview.warnings?.length || 0) > 0 && (
                                    <div
                                        className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-muted-foreground"
                                        data-testid="import-layout-warnings"
                                    >
                                        <AppIcon path={mdiInformationOutline} size="md" className="mt-0.5 shrink-0" />
                                        {/* One line per warning: they are independent
                                            sentences and run together when joined. */}
                                        <ul className="space-y-1">
                                            {preview.warnings?.map((warning, i) => (
                                                <li key={i}>{warning}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {unmatchedCount > 0 && (
                                    <div
                                        className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-muted-foreground"
                                        data-testid="import-layout-unmatched"
                                    >
                                        <AppIcon path={mdiInformationOutline} size="md" className="mt-0.5 shrink-0" />
                                        <span>{t('import.unmatched_notice', { count: unmatchedCount })}</span>
                                    </div>
                                )}

                                {unknownTypes.length > 0 && (
                                    <div
                                        className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-muted-foreground"
                                        data-testid="import-layout-unknown-types"
                                    >
                                        <AppIcon path={mdiAlertCircleOutline} size="md" className="mt-0.5 shrink-0" />
                                        <span>{t('import.unknown_types_notice', { codes: unknownTypes.join(', ') })}</span>
                                    </div>
                                )}

                                {droppedTypes.length > 0 && (
                                    <div
                                        className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-muted-foreground"
                                        data-testid="import-layout-dropped-types"
                                    >
                                        <AppIcon path={mdiAlertCircleOutline} size="md" className="mt-0.5 shrink-0" />
                                        <span>{t('import.dropped_types_notice', { codes: droppedTypes.join(', ') })}</span>
                                    </div>
                                )}

                                {(preview.ignored_sections?.length || 0) > 0 && (
                                    <div
                                        className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-muted-foreground"
                                        data-testid="import-layout-ignored-sections"
                                    >
                                        <AppIcon path={mdiInformationOutline} size="md" className="mt-0.5 shrink-0" />
                                        <span>{t('import.ignored_sections_notice', { sections: preview.ignored_sections?.join(', ') })}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex shrink-0 justify-end gap-2 border-t pt-4">
                        <Button
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            disabled={isBusy}
                            data-testid="import-layout-cancel"
                        >
                            {t('import.cancel')}
                        </Button>
                        <Button
                            onClick={() => { void handleConfirm() }}
                            disabled={!canConfirm}
                            data-testid="import-layout-confirm"
                        >
                            <AppIcon path={mdiUpload} size="sm" className="mr-1" />
                            {isBusy ? t('import.importing') : t('import.confirm')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
